import {
  BodyText,
  Button,
  Checkbox,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@postscript/components';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useResubmitTFNVerification } from '../hooks/useTFNVerification';

const TextSection = styled.div`
  margin-bottom: var(--spacing-3);
`;

const BulletList = styled.ul`
  padding-left: var(--spacing-4);
`;

interface FormValues {
  verified: boolean;
}

const validationSchema = Yup.object({
  verified: Yup.boolean()
    .oneOf([true], 'You must verify that your website meets the criteria')
    .required('You must verify that your website meets the criteria'),
});

interface WebsiteInaccessibleModalProps {
  denialReason: string;
}

export default function WebsiteInaccessibleModal({
  denialReason,
}: WebsiteInaccessibleModalProps): JSX.Element {
  const { hideModal } = useGlobalModal();
  const { mutateAsync: resubmitTFNVerification } = useResubmitTFNVerification();

  const initialValues: FormValues = {
    verified: false,
  };

  const handleSubmit = async () => {
    await resubmitTFNVerification();
    hideModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
    >
      {({ values, setFieldValue, errors, isSubmitting }) => (
        <Form>
          <ModalHeader onCancel={hideModal}>
            Toll-Free Number verification denied
          </ModalHeader>
          <ModalBody>
            <TextSection>
              <BodyText bold size="small">
                Denial reason
              </BodyText>
              <BodyText size="small">{denialReason}</BodyText>
            </TextSection>

            <TextSection>
              <BodyText bold size="small">
                Ensure that your website...
              </BodyText>
              <BulletList>
                <li>
                  <BodyText size="small">
                    Is live and publicly accessible
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small">Is not password protected</BodyText>
                </li>
                <li>
                  <BodyText size="small">
                    Lists 1-2 products with details, images, and pricing
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small">
                    Has at least one product in-stock
                  </BodyText>
                </li>
              </BulletList>
            </TextSection>

            <TextSection>
              <BodyText size="small">
                <strong>
                  Please address the above criteria, then resubmit
                </strong>
                . Verification typically takes up to 5 business days. Your
                website must remain <strong>live</strong> during this time.
              </BodyText>
            </TextSection>

            <Checkbox
              checked={values.verified}
              id="website-verified"
              label="I have verified that my website meets the above criteria"
              name="verified"
              onChange={() => {
                setFieldValue('verified', !values.verified, true);
              }}
            />
            {errors.verified && (
              <BodyText size="x-small">{errors.verified}</BodyText>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !values.verified}
            >
              Resubmit
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
}
